'use client'

import { memo, useCallback, useState } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { CaretDownIcon } from '@phosphor-icons/react/dist/ssr/CaretDown'
import { CheckCircleIcon } from '@phosphor-icons/react/dist/ssr/CheckCircle'
import { Avatar, Button, DropdownMenu } from '@skop-app/lib-front'
import { supportedLocales } from '@skop-app/lib-domain/constants'

import { useUserContext } from '@/context'
import { useSessionRuntime } from '@/context/SessionRuntimeContext'
import { DocLink } from '@/components/atoms'
import { useImpersonationControls } from '@/hooks/useImpersonationControls'
import { selectUser, useUser } from '@/services/user/user.query'
import { useSessionOrchestrator } from '@/hooks/useSessionOrchestrator'

export const UserMenu = memo(() => {
  const { data: user } = useUser(selectUser)
  const { isClient } = useUserContext()

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <Button
          className="flex !h-10 items-center !px-2"
          variant="outline"
          title="Menu Utilisateur"
        >
          <div className="flex min-w-0 items-center gap-2">
            {!isClient || !user ? (
              <>
                <div className="h-8 w-8 animate-pulse rounded-full bg-slate-300" />
                <div className="hidden flex-col items-start sm:flex">
                  <div className="h-3 w-28 animate-pulse rounded bg-slate-300" />
                  <div className="mt-2 h-2 w-20 animate-pulse rounded bg-slate-300" />
                </div>
              </>
            ) : (
              <>
                <Avatar name={user.name} />
                <div className="hidden min-w-0 max-w-36 flex-col items-start sm:flex">
                  <div className="max-w-full truncate text-sm font-medium text-gray-900">
                    {user.name}
                  </div>
                  <div className="max-w-full truncate text-xs text-gray-500">
                    {user.email}
                  </div>
                </div>
              </>
            )}
          </div>
          <CaretDownIcon className="size-4 shrink-0" />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          className="w-[var(--radix-popper-anchor-width)] min-w-[200px]"
        >
          <UserMenuContent />
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
})

UserMenu.displayName = 'UserMenu'

export const UserMenuContent = memo(() => {
  const t = useTranslations('Navigation')
  const router = useRouter()
  const pathname = usePathname()
  const locale = useLocale()

  const { isImpersonating, canImpersonate } = useSessionRuntime()
  const { beginLogout } = useSessionOrchestrator()
  const { startImpersonation, stopImpersonation } = useImpersonationControls()

  const logoutUser = useCallback(async () => {
    await beginLogout('manual')
  }, [beginLogout])
  const marketplacePath = '/search'

  // local UI state for input field
  const [impersonateId, setImpersonateId] = useState('')

  const selectLanguage = useCallback(
    (lang: string) => {
      const nextLocale = lang.toLowerCase()
      const newPathname = pathname.replace(`/${locale}`, `/${nextLocale}`)
      router.push(newPathname)
    },
    [pathname, locale, router],
  )

  return (
    <DropdownMenu.Group>
      <DropdownMenu.Item onClick={() => router.push('/account')}>
        {t('account-settings')}
      </DropdownMenu.Item>
      <DropdownMenu.Sub>
        <DropdownMenu.SubTrigger>
          <span>{t('language-selector')}</span>
        </DropdownMenu.SubTrigger>
        <DropdownMenu.Portal>
          <DropdownMenu.SubContent>
            {supportedLocales.map((lang) => (
              <DropdownMenu.Item
                key={lang.value}
                className={
                  locale.toUpperCase() === lang.value ? 'font-bold' : ''
                }
                onClick={() => selectLanguage(lang.value as string)}
              >
                <span>{lang.label}</span>
                {locale.toUpperCase() === lang.value && (
                  <DropdownMenu.Shortcut>
                    <CheckCircleIcon className="size-4" />
                  </DropdownMenu.Shortcut>
                )}
              </DropdownMenu.Item>
            ))}
          </DropdownMenu.SubContent>
        </DropdownMenu.Portal>
      </DropdownMenu.Sub>
      <DropdownMenu.Item
        onClick={() =>
          router.push(`${process.env.NEXT_PUBLIC_APP_URL}${marketplacePath}`)
        }
      >
        {t('skop-shops')}
      </DropdownMenu.Item>
      <DropdownMenu.Item>
        <DocLink component="menu" />
      </DropdownMenu.Item>
      <DropdownMenu.Separator />
      {isImpersonating ? (
        <>
          <DropdownMenu.Item onClick={() => void stopImpersonation()}>
            Stop impersonation
          </DropdownMenu.Item>
          <DropdownMenu.Separator />
        </>
      ) : canImpersonate ? (
        <>
          <DropdownMenu.Sub>
            <DropdownMenu.SubTrigger>
              <span>Impersonate user…</span>
            </DropdownMenu.SubTrigger>
            <DropdownMenu.Portal>
              <DropdownMenu.SubContent className="flex w-[280px] flex-col gap-2 p-2">
                <input
                  className="rounded border border-gray-300 px-2 py-1 text-sm outline-none"
                  placeholder="Target user ID"
                  value={impersonateId}
                  onChange={(e) => setImpersonateId(e.target.value)}
                />
                <Button
                  size="tiny"
                  disabled={!impersonateId}
                  onClick={async () => {
                    const started = await startImpersonation(impersonateId)
                    if (started) {
                      setImpersonateId('')
                    }
                  }}
                >
                  Start impersonation
                </Button>
              </DropdownMenu.SubContent>
            </DropdownMenu.Portal>
          </DropdownMenu.Sub>
          <DropdownMenu.Separator />
        </>
      ) : null}
      <DropdownMenu.Item onClick={logoutUser}>{t('logout')}</DropdownMenu.Item>
    </DropdownMenu.Group>
  )
})

UserMenuContent.displayName = 'UserMenuContent'
